import {
  Button, CardContent, MenuItem, TextField as MuiTextField, Typography,
} from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import { FC, useState } from 'react'
import {
  CreateButton, Datagrid, List, ListProps, SelectField, useListContext, useNotify, useTranslate,
} from 'react-admin'
import { ACTION_TYPE_LABELS, NOTIFICATION_TYPE_CHOICES } from './NotificationConfigurationForm'
import { ListActionsToolbar } from '../../components'
import { FilterSidebarCard } from '../../components/cards'
import { getUrl, postJson } from '../../providers/data'
import { SyncNowOut } from '../../types/apiRequests'

const SyncNowButton: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const [loading, setLoading] = useState(false)

  const handleClick = async () => {
    setLoading(true)
    try {
      const result = await postJson<SyncNowOut>(
        `${getUrl()}/notification_configuration_sync_now`
      )
      notify(translate('data.syncNowResult', { count: result.processed }), { type: 'success' })
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      startIcon={<SyncIcon />}
      onClick={() => void handleClick()}
      disabled={loading}
      size="small"
      variant="outlined"
    >
      {translate('data.syncNow')}
    </Button>
  )
}

const FilterSidebar: FC = () => {
  const translate = useTranslate()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { filterValues, setFilters } = useListContext()

  return (
    <FilterSidebarCard>
      <CardContent>
        <Typography variant="h6" component="div">
          {translate('filters.title')}
        </Typography>
        <MuiTextField
          select
          fullWidth
          size="small"
          label={translate('data.notificationType')}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, camelcase
          value={filterValues.notification_type ?? ''}
          onChange={(e) => {
            const val = e.target.value
            if (val) {
              // eslint-disable-next-line camelcase
              setFilters({ ...filterValues, notification_type: val }, undefined)
            } else {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, camelcase
              const { notification_type, ...rest } = filterValues
              setFilters(rest, undefined)
            }
          }}
          sx={{ mt: 2 }}
        >
          <MenuItem value="">{translate('filters.all')}</MenuItem>
          {NOTIFICATION_TYPE_CHOICES.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
          ))}
        </MuiTextField>
      </CardContent>
    </FilterSidebarCard>
  )
}

const NotificationConfigurationsList: FC<ListProps> = (props) => (
  <List
    {...props}
    exporter={false}
    aside={<FilterSidebar />}
    actions={
      <ListActionsToolbar i18n="resources.notificationConfigurations.list">
        <SyncNowButton />
        <CreateButton />
      </ListActionsToolbar>
    }
  >
    <Datagrid bulkActionButtons={false} rowClick="edit">
      <SelectField source="notification_type" label="data.notificationType" choices={NOTIFICATION_TYPE_CHOICES} />
      <SelectField
        source="action_type"
        label="data.actionType"
        choices={Object.entries(ACTION_TYPE_LABELS).map(([id, name]) => ({ id, name }))}
      />
    </Datagrid>
  </List>
)

export default NotificationConfigurationsList
